import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete
} from "@mui/material";
import { CommentDto, TradingCounterParty } from "../../../../../../api/Api";
import {
  useAssignees,
  useCounterpartyProgressOverride,
  useUpdateCounterparty
} from "../../../../dataHooks/counterpartyQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../sharedComponents/commentsSection";
import { LifecycleDialog } from "../../sharedComponents/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificCounterparty: TradingCounterParty;
};

type FormData = {
  agreementReview: string;
  agreementReviewAssignee: string;
  collateralForm: string;
  collateralFormAssignee: string;
  collateralFormStatus: string;
  collateralFormStatusAssignee: string;
  amount: string;
  currency: string;
  contactPerson: string;
  cashFlowForecast: string;
  cashFlowForecastAssignee: string;
  marginCallSystem: string;
  marginCallSystemAssignee: string;
  marginCallStatus: string;
  signOffNote: string;
  comments: CommentDto[];
};

export const Treasury: FC<Props> = ({ specificCounterparty }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("counterparty");
  const roles = useRoles();
  const progressOverride = useCounterpartyProgressOverride(
    specificCounterparty,
    "treasury"
  );

  const [formData, setFormData] = useState<FormData>({
    agreementReview:
      specificCounterparty?.treasuryLifecycle?.agreementReview || "",
    agreementReviewAssignee:
      specificCounterparty?.treasuryLifecycle?.agreementReviewAssignee || "",
    collateralForm:
      specificCounterparty?.treasuryLifecycle?.collateralForm || "",
    collateralFormAssignee:
      specificCounterparty?.treasuryLifecycle?.collateralFormAssignee || "",
    collateralFormStatus:
      specificCounterparty?.treasuryLifecycle?.collateralFormStatus || "",
    collateralFormStatusAssignee:
      specificCounterparty?.treasuryLifecycle?.collateralFormStatusAssignee ||
      "",
    amount: specificCounterparty?.treasuryLifecycle?.amount?.toString() || "",
    currency: specificCounterparty?.treasuryLifecycle?.currency || "",
    contactPerson: specificCounterparty?.treasuryLifecycle?.contactPerson || "",
    cashFlowForecast:
      specificCounterparty?.treasuryLifecycle?.cashFlowForecast === true
        ? "completed"
        : specificCounterparty?.treasuryLifecycle?.cashFlowForecast === false
          ? "not_completed"
          : "",
    cashFlowForecastAssignee:
      specificCounterparty?.treasuryLifecycle?.cashFlowForecastAssignee || "",
    marginCallSystem:
      specificCounterparty?.treasuryLifecycle?.marginCallSystem || "",
    marginCallSystemAssignee:
      specificCounterparty?.treasuryLifecycle?.marginCallSystemAssignee || "",
    marginCallStatus:
      specificCounterparty?.treasuryLifecycle?.marginCallStatus || "",
    signOffNote: specificCounterparty?.treasuryLifecycle?.signOffNote || "",
    comments: specificCounterparty?.treasuryLifecycle?.comments || []
  });

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const cashFlowForecastToBool = (value: string): boolean | undefined =>
    value === "completed"
      ? true
      : value === "not_completed"
        ? false
        : undefined;

  const treasuryAssignees = useAssignees("treasury");

  // Single source of truth for the treasury slice. Every write path (save,
  // complete, comment add/delete) builds the slice from here so no field can be
  // dropped by one path. See CLAUDE.md: field-by-field dialogs must thread every
  // field into all three write paths.
  const buildTreasuryLifecycle = (
    values: FormData & { complete?: boolean }
  ) => ({
    agreementReview: values.agreementReview,
    agreementReviewAssignee: values.agreementReviewAssignee,
    collateralForm: values.collateralForm,
    collateralFormAssignee: values.collateralFormAssignee,
    collateralFormStatus: values.collateralFormStatus,
    collateralFormStatusAssignee: values.collateralFormStatusAssignee,
    amount: values.amount || undefined,
    currency: values.currency,
    contactPerson: values.contactPerson || undefined,
    cashFlowForecast: cashFlowForecastToBool(values.cashFlowForecast),
    cashFlowForecastAssignee: values.cashFlowForecastAssignee,
    marginCallSystem: values.marginCallSystem,
    marginCallSystemAssignee: values.marginCallSystemAssignee,
    marginCallStatus: values.marginCallStatus,
    signOffNote: values.signOffNote,
    comments: values.comments,
    ...(values.complete ? { complete: true } : {})
  });

  const updateCounterparty = useUpdateCounterparty({
    onSuccess: handleClose
  });
  const commentUpdate = useUpdateCounterparty();
  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (updatedComments) => ({
      ...specificCounterparty,
      treasuryLifecycle: buildTreasuryLifecycle({
        ...formData,
        comments: updatedComments
      })
    })
  });

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "collateralForm") {
        newData.collateralFormStatus = "";
      }
      if (field === "marginCallSystem" && value !== "needed") {
        newData.marginCallStatus = "";
      }
      return newData;
    });
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificCounterparty.progress?.treasury,
    canSetInProgress: ownsLifecycle(roles, "treasury"),
    buildEntity: (values, progress) => ({
      ...specificCounterparty,
      progress: { ...specificCounterparty.progress, treasury: progress },
      treasuryLifecycle: buildTreasuryLifecycle(values)
    }),
    mutate: updateCounterparty.mutate
  });

  const getCollateralFormLabel = (formType: string): string => {
    const labels: Record<string, string> = {
      cash_deposit: "Cash Deposit",
      letter_of_credit: "Letter of Credit",
      bank_guarantee: "Bank Guarantee",
      parent_company_guarantee: "Parent Company Guarantee"
    };
    return labels[formType] || "";
  };

  const getCollateralFormStatusOptions = () => {
    switch (formData.collateralForm) {
      case "cash_deposit":
        return [
          { value: "in_progress", label: "In progress" },
          { value: "completed", label: "Completed" },
          { value: "rejected", label: "Rejected" }
        ];
      case "letter_of_credit":
      case "bank_guarantee":
        return [
          { value: "in_progress", label: "In progress" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" }
        ];
      case "parent_company_guarantee":
        return [
          { value: "wording_requested", label: "Wording requested" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" }
        ];
      default:
        return [];
    }
  };

  const open = location.pathname.includes("treasury");

  const marginCallSatisfied =
    formData.marginCallSystem === "not_needed" ||
    (formData.marginCallSystem === "needed" &&
      formData.marginCallStatus?.length);

  const collateralRequired =
    specificCounterparty.marketAccessLifecycle?.collateral === "needed";

  const canComplete =
    (collateralRequired
      ? formData.amount &&
        formData.currency &&
        formData.collateralForm &&
        formData.collateralFormStatus
      : true) &&
    formData.agreementReview &&
    formData.cashFlowForecast &&
    marginCallSatisfied;

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Treasury"
      progressOverride={progressOverride}
      maxWidth="md"
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateCounterparty.isPending
      }}
    >
      <Box
        sx={{
          paddingTop: 2,
          gap: 2,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Box display="flex" gap={2}>
          <FormControl sx={{ flex: 1 }} size="medium" required>
            <InputLabel>
              Agreement review (collateral/liquidity terms)
            </InputLabel>
            <Select
              label="Agreement review (collateral/liquidity terms)"
              value={formData.agreementReview}
              onChange={(e) => handleChange("agreementReview", e.target.value)}
            >
              <MenuItem value="initiated">
                <Typography variant="body1" component="span">
                  Initiated
                </Typography>
              </MenuItem>
              <MenuItem value="approved">
                <Typography variant="body1" component="span">
                  Approved
                </Typography>
              </MenuItem>
              <MenuItem value="rejected">
                <Typography variant="body1" component="span">
                  Rejected
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={treasuryAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              treasuryAssignees.data?.find(
                (a) => a.email === formData.agreementReviewAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("agreementReviewAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agreement review assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {collateralRequired && (
          <>
            <Box display="flex" gap={2}>
              <FormControl sx={{ flex: 1 }} size="medium" required>
                <InputLabel>Collateral form</InputLabel>
                <Select
                  label="Collateral form"
                  value={formData.collateralForm}
                  onChange={(e) =>
                    handleChange("collateralForm", e.target.value)
                  }
                >
                  <MenuItem value="cash_deposit">
                    <Typography variant="body1" component="span">
                      Cash deposit
                    </Typography>
                  </MenuItem>
                  <MenuItem value="letter_of_credit">
                    <Typography variant="body1" component="span">
                      Letter of Credit
                    </Typography>
                  </MenuItem>
                  <MenuItem value="bank_guarantee">
                    <Typography variant="body1" component="span">
                      Bank Guarantee
                    </Typography>
                  </MenuItem>
                  <MenuItem value="parent_company_guarantee">
                    <Typography variant="body1" component="span">
                      Parent Company Guarantee
                    </Typography>
                  </MenuItem>
                </Select>
              </FormControl>

              <Autocomplete
                sx={{ flex: 1 }}
                options={treasuryAssignees.data || []}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                value={
                  treasuryAssignees.data?.find(
                    (a) => a.email === formData.collateralFormAssignee
                  ) || null
                }
                onChange={(_, newValue) =>
                  handleChange("collateralFormAssignee", newValue?.email || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Collateral form assignee"
                    size="medium"
                  />
                )}
              />
            </Box>

            {formData.collateralForm && (
              <Box display="flex" gap={2}>
                <FormControl sx={{ flex: 1 }} size="medium" required>
                  <InputLabel>
                    {getCollateralFormLabel(formData.collateralForm)} status
                  </InputLabel>
                  <Select
                    label={`${getCollateralFormLabel(formData.collateralForm)} status`}
                    value={formData.collateralFormStatus}
                    onChange={(e) =>
                      handleChange("collateralFormStatus", e.target.value)
                    }
                  >
                    {getCollateralFormStatusOptions().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Typography variant="body1" component="span">
                          {option.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Autocomplete
                  sx={{ flex: 1 }}
                  options={treasuryAssignees.data || []}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.email})`
                  }
                  value={
                    treasuryAssignees.data?.find(
                      (a) => a.email === formData.collateralFormStatusAssignee
                    ) || null
                  }
                  onChange={(_, newValue) =>
                    handleChange(
                      "collateralFormStatusAssignee",
                      newValue?.email || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Collateral form status assignee"
                      size="medium"
                    />
                  )}
                />
              </Box>
            )}

            <TextField
              required
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
            />

            <TextField
              required
              label="Currency"
              value={formData.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              placeholder="e.g., USD, EUR, GBP"
            />
          </>
        )}

        <TextField
          label="Contact person"
          value={formData.contactPerson}
          onChange={(e) => handleChange("contactPerson", e.target.value)}
        />

        <Box display="flex" gap={2}>
          <FormControl sx={{ flex: 1 }} size="medium" required>
            <InputLabel>Margin call system</InputLabel>
            <Select
              label="Margin call system"
              value={formData.marginCallSystem}
              onChange={(e) => handleChange("marginCallSystem", e.target.value)}
            >
              <MenuItem value="needed">
                <Typography variant="body1" component="span">
                  Needed
                </Typography>
              </MenuItem>
              <MenuItem value="not_needed">
                <Typography variant="body1" component="span">
                  Not needed
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={treasuryAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              treasuryAssignees.data?.find(
                (a) => a.email === formData.marginCallSystemAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("marginCallSystemAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Margin call system assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {formData.marginCallSystem === "needed" && (
          <Box display="flex" gap={2}>
            <FormControl sx={{ flex: 1 }} size="medium" required>
              <InputLabel>Margin call status</InputLabel>
              <Select
                label="Margin call status"
                value={formData.marginCallStatus}
                onChange={(e) =>
                  handleChange("marginCallStatus", e.target.value)
                }
              >
                <MenuItem value="completed">
                  <Typography variant="body1" component="span">
                    Completed
                  </Typography>
                </MenuItem>
                <MenuItem value="not_completed">
                  <Typography variant="body1" component="span">
                    Not completed
                  </Typography>
                </MenuItem>
                <MenuItem value="denied">
                  <Typography variant="body1" component="span">
                    Denied
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Cash flow forecast</InputLabel>
            <Select
              label="Cash flow forecast"
              value={formData.cashFlowForecast}
              onChange={(e) => handleChange("cashFlowForecast", e.target.value)}
            >
              <MenuItem value="completed">
                <Typography variant="body1" component="span">
                  Completed
                </Typography>
              </MenuItem>
              <MenuItem value="not_completed">
                <Typography variant="body1" component="span">
                  Not completed
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={treasuryAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              treasuryAssignees.data?.find(
                (a) => a.email === formData.cashFlowForecastAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("cashFlowForecastAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cash flow forecast assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
